import { useMemo } from 'react'
import { useBackendQuery } from '@thirstycamel/ui'

import type {
  MissedSavingsRequest,
  MissedSavingsResponse,
} from '@ts/core/src/modules/humpclub/dto/missed-savings.dto'
import { useStore } from '../store/hooks'
import { useUser } from './useUser'

export type UseMissedSavingsResult = {
  totalDiscountAmount: number // cents
  isLoading: boolean
  hasPotentialSavings: boolean
}

export function useMissedSavings(): UseMissedSavingsResult {
  const { isLoggedIn } = useUser()
  const cart = useStore(s => s.cart.cart)
  const isFetchingCart = useStore(s => s.cart.isFetching)
  const location = useStore(s => s.location)

  const region = cart?.store?.region || location?.region || 'VIC'
  const store = cart?.store?.slug

  const missedSavingsItems = useMemo(
    () =>
      (cart?.cartItems || [])
        .filter((ci: any) => ci?.product && !ci?.parent)
        .map((ci: any) => ({
          cartItemId: ci?.id,
          barcodes: ci?.product?.barcodes || [],
          unitPriceCents: ci?.singlePrice ?? ci?.specialPricing?.price ?? ci?.pricing?.price ?? 0,
          qty: ci?.qty ?? 0,
        })),
    [cart],
  )

  const hasCartItems = missedSavingsItems.length > 0

  const shouldFetchMissedSavings = !isLoggedIn && !!cart?.id && hasCartItems

  const requestState = useMemo<MissedSavingsRequest['state']>(() => {
    const validStates = ['NSW', 'VIC', 'NSWBR', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'] as const

    if (validStates.includes(region as (typeof validStates)[number])) {
      return region as MissedSavingsRequest['state']
    }

    return 'VIC' as MissedSavingsRequest['state']
  }, [region])

  const requestPayload = useMemo<MissedSavingsRequest>(
    () => ({
      state: requestState,
      store,
      items: missedSavingsItems,
    }),
    [requestState, store, missedSavingsItems],
  )

  const { data: missedSavings, isFetching: isFetchingMissedSavings } =
    useBackendQuery<MissedSavingsResponse>(
      ['/humpclub/missed-savings', { method: 'POST', data: requestPayload }],
      { enabled: shouldFetchMissedSavings },
    )

  const result = useMemo<UseMissedSavingsResult>(() => {
    if (isLoggedIn || !hasCartItems) {
      return {
        totalDiscountAmount: 0,
        isLoading: isFetchingCart,
        hasPotentialSavings: false,
      }
    }

    const totalDiscountAmount = missedSavings?.totalDiscountAmountCents || 0

    return {
      totalDiscountAmount,
      isLoading: isFetchingCart || isFetchingMissedSavings,
      hasPotentialSavings: !!missedSavings?.hasPotentialSavings && totalDiscountAmount > 0,
    }
  }, [hasCartItems, isLoggedIn, missedSavings, isFetchingCart, isFetchingMissedSavings])

  return result
}

export default useMissedSavings
